import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../hooks/useAuthStore";
import { useAuthData } from "../hooks/useAuthData";
import { useMaintenanceStatus } from "../hooks/useMaintenanceStatus";

/**
 * Paths that should remain reachable even when maintenance mode is active.
 * - /maintenance: the maintenance screen itself
 * - /auth/admin/login: so admins can still log in and reach the dashboard
 */
const ALLOWED_PATHS = ["/maintenance", "/auth/admin/login"];

/**
 * Wraps the application router and redirects non-admin users to the maintenance
 * page whenever the platform is in maintenance mode. Admins can continue using
 * the app normally so they can control the toggle from the admin dashboard.
 */
export default function MaintenanceGuard({ children }) {
  const location = useLocation();
  const { isAdmin, token } = useAuthStore();
  const { data, isLoading: isMaintenanceLoading, isError } = useMaintenanceStatus();

  // Load auth at the guard level so we know the user's role before deciding
  // whether to redirect to the maintenance page. Without this, admins could be
  // redirected before their session has been resolved.
  const { isLoading: isAuthLoading } = useAuthData();

  // Don't block while we're checking the maintenance flag or while the auth
  // session is still being resolved for users that already have a token.
  if (isMaintenanceLoading || (token && isAuthLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9FAFB]">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#61013B]" />
      </div>
    );
  }

  // If the status check fails, fail open so users aren't permanently locked out.
  if (isError) {
    return children;
  }

  const maintenanceActive = data?.maintenanceMode === true;

  // Admins are never redirected to maintenance; they need access to turn it off.
  if (isAdmin) {
    return children;
  }

  const currentPath = location.pathname;

  // Redirect non-admin users away from protected pages when maintenance is on.
  if (maintenanceActive && !ALLOWED_PATHS.includes(currentPath)) {
    return <Navigate to="/maintenance" replace />;
  }

  // Once maintenance is over, send users back home from the maintenance screen.
  if (!maintenanceActive && currentPath === "/maintenance") {
    return <Navigate to="/" replace />;
  }

  return children;
}
